import {
	boardAnswer,
	chipText,
	clearSlot,
	placeChip,
	startSlots,
} from "@aybuben/client/lesson/compose.ts";
import { capitalise } from "@aybuben/core/content/course.ts";
import { isBuild, typedTarget, type ComposeAnswer } from "@aybuben/core/exercises/compose/logic.ts";
import { faceText } from "@aybuben/core/face.ts";
import type { ComposeStep } from "@aybuben/core/lesson.ts";
import { checkTyped, isTyped, typedAnswer } from "@aybuben/core/writing/check.ts";
import { useState } from "react";

import { haptic } from "../../haptics.ts";
import { useStrings } from "../../i18n/index.tsx";
import { COL, FOCUS } from "../../theme.ts";
import { G } from "../../ui/primitives.tsx";
import { SpeakerRow } from "../../ui/SpeakerRow.tsx";
import { useWriting } from "../../ui/writing/context.tsx";
import { TypedDiff } from "../../ui/writing/TypedDiff.tsx";
import { TypedInput } from "../../ui/writing/TypedInput.tsx";
import { WriteSwitch } from "../../ui/writing/WriteSwitch.tsx";
import { StepHead } from "../kit.tsx";
import { registerView, type ExerciseViewProps, type FeedbackProps } from "../view-registry.ts";

/** The sentence as it should read, for the feedback panel. */
const sentenceText = (step: ComposeStep): string =>
	capitalise(step.tokens.map((t) => faceText(t.face)).join(" "));

function ComposeView(props: ExerciseViewProps<ComposeStep, ComposeAnswer>) {
	const { step, setAnswer, status, retry } = props;
	const t = useStrings();
	const w = useWriting();
	const build = isBuild(step);
	return (
		<div>
			<StepHead
				title={build ? t("lesson.composeBuild") : t("lesson.composeCloze")}
				retry={retry}
				retryNote={t("lesson.retryNote")}
			/>
			<div className="mt-2">
				<WriteSwitch disabled={!!status} onSwitch={() => setAnswer(null)} />
			</div>
			{step.prompt === "gloss" ? (
				<div className="mt-3">
					<SpeakerRow text={[{ t: step.gloss }]} status={status} />
				</div>
			) : (
				<>
					{step.prompt !== "sound" && (
						<p className="mt-2 text-base" style={{ color: COL.mute }}>
							{step.gloss}
						</p>
					)}
					{step.sound && (
						<div className="mt-3">
							<SpeakerRow asset={step.sound} status={status} />
						</div>
					)}
				</>
			)}

			{/* Keyed by mode: switching drops the other mode's half-made answer. */}
			{w.write === "keys" ? (
				<ComposeTyped key="keys" {...props} />
			) : (
				<ComposeChips key="chips" {...props} />
			)}
		</div>
	);
}

/**
 * A cloze shows its given words, the blank filling from the answer as the
 * learner types; a build is the field alone.
 */
function ComposeTyped({
	step,
	answer,
	setAnswer,
	status,
	onSubmit,
}: ExerciseViewProps<ComposeStep, ComposeAnswer>) {
	const blanks = new Set(step.blanks);
	const typed = isTyped(answer) ? answer.typed.trim() : "";
	return (
		<>
			{!isBuild(step) && (
				<div className="mt-6 flex flex-wrap items-center gap-2" data-testid="cloze-line">
					{step.tokens.map((tok, i) => {
						const shown = blanks.has(i) ? typed : faceText(tok.face);
						return (
							<span
								key={i}
								className="rounded-xl px-3 py-2"
								style={{
									minWidth: 64,
									minHeight: 48,
									background: blanks.has(i) ? COL.well : COL.card,
									border: `2px ${blanks.has(i) ? "dashed" : "solid"} ${blanks.has(i) ? COL.edge : "transparent"}`,
								}}
							>
								<G size={26}>{i === 0 ? capitalise(shown) : shown}</G>
							</span>
						);
					})}
				</div>
			)}
			<TypedInput
				status={status}
				onSubmit={onSubmit}
				onChange={(next) => setAnswer(typedAnswer(next))}
			/>
		</>
	);
}

function ComposeChips({ step, setAnswer, status }: ExerciseViewProps<ComposeStep, ComposeAnswer>) {
	const t = useStrings();
	const blanks = new Set(step.blanks);
	// Slot contents, starting from the given tokens. Local rather than lifted:
	// the shell's `answer` is the *completed* sequence or null, and a
	// half-filled board is neither. Remounted per step by the shell's
	// key={step.uid}, so a requeued retry starts empty again.
	const [slots, setSlots] = useState(() => startSlots(step));

	const commit = (next: (string | null)[] | null) => {
		if (!next) return;
		setSlots(next);
		setAnswer(boardAnswer(next));
	};

	const place = (id: string) => {
		if (!status) commit(placeChip(step, slots, id));
	};

	const clear = (i: number) => {
		if (!status) commit(clearSlot(step, slots, i));
	};

	const used = new Set(slots.filter((x): x is string => x !== null));
	const textOf = (id: string) => chipText(step, id);

	return (
		<div>
			<div className="mt-6 flex flex-wrap items-center gap-2">
				{step.tokens.map((tok, i) => {
					const id = slots[i] ?? null;
					const filled = id !== null;
					const isBlank = blanks.has(i);
					const shown = filled ? textOf(id) : "";
					return (
						<button
							key={i}
							data-testid={`slot-${i}`}
							onClick={() => clear(i)}
							disabled={!isBlank || !filled || !!status}
							aria-label={
								!filled
									? t("lesson.composeSlot")
									: isBlank
										? t("lesson.composeRemove", { word: shown })
										: t("lesson.composeGiven", { word: shown })
							}
							className={`rounded-xl px-3 py-2 ${FOCUS}`}
							style={{
								minWidth: 64,
								minHeight: 48,
								background: filled ? COL.card : COL.well,
								border: `2px ${filled ? "solid" : "dashed"} ${isBlank ? COL.line : "transparent"}`,
							}}
						>
							{filled && <G size={26}>{i === 0 ? capitalise(shown) : shown}</G>}
						</button>
					);
				})}
			</div>

			<div className="mt-8 flex flex-wrap gap-2">
				{step.bank.map((o) => (
					<button
						key={o.id}
						data-testid={`bank-${o.id}`}
						onClick={() => {
							haptic("tap");
							place(o.id);
						}}
						disabled={used.has(o.id) || !!status}
						className={`rounded-xl px-3 py-2 transition active:translate-y-0.5 ${FOCUS}`}
						style={{
							minHeight: 48,
							background: used.has(o.id) ? COL.well : COL.card,
							border: `2px solid ${used.has(o.id) ? COL.well : COL.line}`,
							boxShadow: used.has(o.id) ? "none" : `0 4px 0 ${COL.edge}`,
							color: used.has(o.id) ? COL.spent : COL.ink,
						}}
					>
						<G size={26}>{faceText(o.face)}</G>
					</button>
				))}
			</div>
		</div>
	);
}

function ComposeFeedback({ step, ok, answer }: FeedbackProps<ComposeStep, ComposeAnswer>) {
	return (
		<div className="mt-2">
			<G size={26}>{sentenceText(step)}</G>
			<div className="text-sm leading-snug" style={{ color: COL.mute }}>
				{step.gloss}
			</div>
			{!ok && isTyped(answer) && (
				<TypedDiff verdict={checkTyped(typedTarget(step), answer.typed)} />
			)}
		</div>
	);
}

registerView<ComposeStep, ComposeAnswer>("compose", {
	View: ComposeView,
	Feedback: ComposeFeedback,
	action: "check",
});
